import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  trackCartCleared,
  trackCartItemAdded,
  trackCartItemQuantityChanged,
  trackCartItemRemoved,
  trackCartItemReplaced,
  trackCartExpiryWarningShown,
  trackCartExpiryDismissed,
} from "../utils/analytics";
import { cacheClear } from "../utils/sessionCache";

const CartContext = createContext(null);

const CART_STORAGE_KEY = "btc-cart";

const roundMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
};

// What makes two carts the same as far as the server copy is concerned.
export const cartItemsKey = (items, taxRateFor) =>
  JSON.stringify((items || []).map((i) => [i.skuCode, i.quantity, i.unitPrice, taxRateFor ? taxRateFor(i) : 0]));

const createCartItemId = (product, variant = null, selectedOptions = []) => {
  const baseId = product?.id || product?.skuCode || product?.name || "item";
  const variantId = variant?.id || variant?.skuCode || "default";
  const optionIds = (Array.isArray(selectedOptions) ? selectedOptions : [])
    .map((option) => option.id)
    .filter(Boolean)
    .sort()
    .join(",");
  return `${baseId}::${variantId}::${optionIds}`;
};

const normalizeSelectedOption = (option) => {
  const unitPrice = roundMoney(option?.unitPrice ?? option?.price ?? 0);
  return {
    id: option?.id || option?.optionId || option?.skuCode || option?.title,
    optionId: option?.optionId || option?.id || "",
    itemId: option?.itemId || "",
    skuCode: option?.skuCode || "",
    name: option?.name || option?.title || "",
    title: option?.title || option?.name || "",
    groupId: option?.groupId || "",
    groupName: option?.groupName || "",
    price: unitPrice,
    unitPrice,
    quantity: 1,
  };
};

const getSingleVariant = (product) => {
  const variationGroups = Array.isArray(product?.variations) ? product.variations : [];
  const variantGroup = variationGroups.find(g => g?.isVariantGroup);
  if (variantGroup && (variantGroup.options?.length || 0) === 1) {
    return variantGroup.options[0];
  }
  return null;
};

export function CartProvider({ cafeCode, children }) {
  const [items, setItems] = useState([]);
  const [taxRatesById, setTaxRatesById] = useState({});
  // What the server says the cart costs (incl. GST on the post-discount
  // amount); null until the first save comes back.
  const [serverTotals, setServerTotals] = useState(null);
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [tableNumber, setTableNumber] = useState("");
  // The table exactly as the cafe's POS names it ("Table no. 7"); this, not the number, becomes the POS order title.
  const [tableName, setTableName] = useState("");
  // How the customer is having this order: "dinein" | "takeaway" | "" (not yet
  // chosen). Picked before the table so the barista knows whether to pack it —
  // a takeaway order needs no table and is tagged so it isn't over-packed.
  const [orderType, setOrderType] = useState("");
  // Circle points the customer chose to redeem on this order, and the
  // backend cartId once the cart has been persisted for checkout/payment.
  const [appliedLoyaltyPoints, setAppliedLoyaltyPoints] = useState(0);
  // Server-confirmed savings on the cart (set from the API response after a
  // real apply call — not a client-side guess). Only one of the two is ever
  // set at a time; applying either clears the other (mirrors the app's
  // "choose one savings method" tabs). Not persisted to localStorage —
  // always re-applied fresh, since eligibility/balance can change.
  // Shape: { id, name, amount }
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  // Shape: { id, name, cost, amount }
  const [appliedReward, setAppliedReward] = useState(null);
  const [cafeMenuCartId, setCafeMenuCartId] = useState(null);
  const [isCartTrackingEnabled, setCartTrackingEnabled] = useState(false);
  // Bumped every time the cart is cleared, so consumers (e.g. menu filters)
  // can react to a clear from any source.
  const [clearSignal, setClearSignal] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  const storageKey = `${CART_STORAGE_KEY}-${cafeCode}`;
  const metaKey = `${CART_STORAGE_KEY}-meta-${cafeCode}`;

  useEffect(() => {
    setCartTrackingEnabled(false);
  }, [cafeCode]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          setItems(parsed);
        }
      }
      const meta = localStorage.getItem(metaKey);
      if (meta) {
        const parsed = JSON.parse(meta);
        setSpecialInstructions(parsed.specialInstructions || "");
        setPhoneNumber(parsed.phoneNumber || "");
        setTableNumber((parsed.tableNumber || "").replace(/[^\d]/g, ""));
        setTableName(typeof parsed.tableName === "string" ? parsed.tableName : "");
        setOrderType(parsed.orderType === "takeaway" || parsed.orderType === "dinein" ? parsed.orderType : "");
        setCafeMenuCartId(parsed.cafeMenuCartId || null);
      }
    } catch { /* ignore */ }
    setHydrated(true);
  }, [storageKey, metaKey]);

  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(items));
      localStorage.setItem(
        metaKey,
        JSON.stringify({ specialInstructions, phoneNumber, tableNumber, tableName, orderType, cafeMenuCartId })
      );
    } catch { /* ignore quota errors */ }
  }, [items, specialInstructions, phoneNumber, tableNumber, tableName, orderType, cafeMenuCartId, hydrated, storageKey, metaKey]);

  // The cart is saved to the backend only when the customer commits to it (the Review step in
  // CartPage) — never as a side effect of browsing, tapping + / − or typing. This record lives here,
  // not in CartPage, so leaving the cart and coming back (e.g. after the Customise sheet) does not
  // look like a new cart and trigger another save.
  const savedCartRef = useRef({ signature: "", itemsKey: "", inflight: "" });
  const getSavedCart = useCallback(() => savedCartRef.current, []);
  // true = go ahead and save; false = this exact cart is already saved or being saved right now.
  const claimCartSave = useCallback((signature) => {
    const saved = savedCartRef.current;
    if (saved.signature === signature || saved.inflight === signature) return false;
    saved.inflight = signature;
    return true;
  }, []);
  // Returns false when a newer save has since been claimed (so this answer is stale and must be ignored).
  const finishCartSave = useCallback((signature, itemsKey, ok) => {
    const saved = savedCartRef.current;
    const latest = saved.inflight === signature;
    if (latest) saved.inflight = "";
    if (ok && latest) {
      saved.signature = signature;
      saved.itemsKey = itemsKey;
    }
    return latest;
  }, []);
  const resetSavedCart = useCallback(() => {
    savedCartRef.current = { signature: "", itemsKey: "", inflight: "" };
  }, []);

  const addToCart = useCallback((product, variant = null, selectedOptions = [], quantity = 1) => {
    if (!product) return;
    const addQty = Math.max(1, Math.floor(Number(quantity) || 1));

    const optionList = (Array.isArray(selectedOptions) ? selectedOptions : []).map(
      normalizeSelectedOption
    );
    const optionsExtra = optionList.reduce(
      (sum, o) => sum + Number(o?.unitPrice ?? o?.price ?? 0),
      0
    );
    const baseUnitPriceRaw = variant?.price ?? product?.price ?? 0;
    const baseUnitPrice = Number.isFinite(Number(baseUnitPriceRaw))
      ? Number(baseUnitPriceRaw)
      : 0;
    const unitPrice = roundMoney(baseUnitPrice + optionsExtra);
    const id = createCartItemId(product, variant, optionList);
    const variantTitle = variant?.title || "";
    const name = variantTitle ? `${product.name} (${variantTitle})` : product.name;
    const skuCode = variant?.skuCode || product?.skuCode || "";
    const itemId = variant?.id || product?.id || product?.skuCode || product?.name;
    const imageUrl = product?.imageUrl || product?.productDefaultImage || "";
    const categoryName = product?.categoryName || product?.parentName || "";
    const subCategoryName = product?.subCategoryName || "";
    const taxTypeIds = Array.isArray(variant?.taxTypeIds)
      ? variant.taxTypeIds
      : Array.isArray(product?.taxTypeIds)
      ? product.taxTypeIds
      : [];
    const isPriceIncludesTax = Boolean(
      variant?.isPriceIncludesTax ?? product?.isPriceIncludesTax
    );

    const newItem = {
      id,
      itemId,
      productId: product?.id || product?.skuCode || product?.name,
      skuCode,
      name,
      shortName: product?.name || name,
      longName: name,
      productName: product?.name || "",
      categoryName,
      subCategoryName,
      variantTitle,
      selectedOptions: optionList,
      quantity: addQty,
      baseUnitPrice,
      optionTotal: roundMoney(optionsExtra),
      unitPrice,
      taxTypeIds,
      isPriceIncludesTax,
      imageUrl,
      tags: Array.isArray(product?.tags) ? product.tags : [],
    };

    const existing = items.find((it) => it.id === id);
    const nextItems = existing
      ? items.map((it) => (it.id === id ? { ...it, quantity: it.quantity + addQty } : it))
      : [...items, newItem];

    setItems(nextItems);

    const trackedItem = existing
      ? { ...existing, quantity: existing.quantity + addQty }
      : newItem;

    if (isCartTrackingEnabled) {
      trackCartItemAdded({
        cafeId: cafeCode,
        item: trackedItem,
        quantity: addQty,
        addSource: optionList.length > 0 || variantTitle ? "customization_modal" : "quick_add",
        hasCustomization: optionList.length > 0 || Boolean(variantTitle),
        itemCount: nextItems.reduce((sum, it) => sum + it.quantity, 0),
        lineCount: nextItems.length,
        subtotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        grandTotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, tableNumber, phoneNumber, isCartTrackingEnabled]);

  const updateQuantity = useCallback((itemId, delta) => {
    const currentItem = items.find((it) => it.id === itemId);
    if (!currentItem || !delta) return;

    const nextQuantity = currentItem.quantity + delta;
    const nextItems = items
      .map((it) => (it.id !== itemId ? it : { ...it, quantity: nextQuantity }))
      .filter((it) => it.quantity > 0);

    setItems(nextItems);

    if (nextQuantity <= 0) {
      if (isCartTrackingEnabled) {
        trackCartItemRemoved({
          cafeId: cafeCode,
          item: currentItem,
          removedQuantity: currentItem.quantity,
          removalSource: "quantity_control",
          itemCount: nextItems.reduce((sum, it) => sum + it.quantity, 0),
          lineCount: nextItems.length,
          subtotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
          grandTotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
          hasTableNumber: Boolean(tableNumber),
          hasPhoneNumber: Boolean(phoneNumber),
        });
      }
      return;
    }

    if (isCartTrackingEnabled) {
      trackCartItemQuantityChanged({
        cafeId: cafeCode,
        item: currentItem,
        action: delta > 0 ? "increase" : "decrease",
        delta,
        resultingQuantity: nextQuantity,
        hasCustomization:
          Boolean(currentItem.variantTitle) ||
          (Array.isArray(currentItem.selectedOptions) && currentItem.selectedOptions.length > 0),
        itemCount: nextItems.reduce((sum, it) => sum + it.quantity, 0),
        lineCount: nextItems.length,
        subtotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        grandTotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, tableNumber, phoneNumber, isCartTrackingEnabled]);

  const removeItem = useCallback((itemId) => {
    const currentItem = items.find((it) => it.id === itemId);
    if (!currentItem) return;
    const nextItems = items.filter((it) => it.id !== itemId);
    setItems(nextItems);

    if (isCartTrackingEnabled) {
      trackCartItemRemoved({
        cafeId: cafeCode,
        item: currentItem,
        removedQuantity: currentItem.quantity,
        removalSource: "remove_button",
        itemCount: nextItems.reduce((sum, it) => sum + it.quantity, 0),
        lineCount: nextItems.length,
        subtotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        grandTotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, tableNumber, phoneNumber, isCartTrackingEnabled]);

  const clearCart = useCallback((meta = {}) => {
    const currentItems = items;
    const currentItemCount = currentItems.reduce((sum, it) => sum + it.quantity, 0);
    const currentSubtotal = currentItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);

    setItems([]);
    setSpecialInstructions("");
    setPhoneNumber("");
    setTableNumber("");
    setTableName("");
    setOrderType("");
    setAppliedLoyaltyPoints(0);
    setAppliedDiscount(null);
    setAppliedReward(null);
    setCafeMenuCartId(null);
    savedCartRef.current = { signature: "", itemsKey: "", inflight: "" };
    cacheClear();
    setClearSignal((n) => n + 1);
    if (isCartTrackingEnabled) {
      trackCartCleared({
        cafeId: cafeCode,
        clearSource: meta.clearSource || "unknown",
        itemCount: currentItemCount,
        lineCount: currentItems.length,
        subtotal: currentSubtotal,
        grandTotal: currentSubtotal,
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, phoneNumber, tableNumber, isCartTrackingEnabled]);

  const INACTIVITY_MS = 5 * 60 * 1000;
  const WARNING_MS = 60 * 1000;
  const [cartExpiring, setCartExpiring] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const warningTimerRef = useRef(null);

  const touchActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (cartExpiring) {
      setCartExpiring(false);
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    }
  }, [cartExpiring]);

  useEffect(() => {
    let throttled = false;
    const handler = () => {
      if (throttled) return;
      throttled = true;
      lastActivityRef.current = Date.now();
      if (cartExpiring) {
        setCartExpiring(false);
        if (warningTimerRef.current) {
          clearTimeout(warningTimerRef.current);
          warningTimerRef.current = null;
        }
      }
      setTimeout(() => { throttled = false; }, 5000);
    };
    document.addEventListener("touchstart", handler, { passive: true });
    document.addEventListener("click", handler, { passive: true });
    document.addEventListener("scroll", handler, { passive: true });
    return () => {
      document.removeEventListener("touchstart", handler);
      document.removeEventListener("click", handler);
      document.removeEventListener("scroll", handler);
    };
  }, [cartExpiring]);

  useEffect(() => {
    const check = setInterval(() => {
      if (items.length === 0) return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= INACTIVITY_MS && !cartExpiring) {
        setCartExpiring(true);
        const currentItems = items;
        const currentItemCount = currentItems.reduce((sum, it) => sum + it.quantity, 0);
        const currentSubtotal = currentItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
        if (isCartTrackingEnabled) {
          trackCartExpiryWarningShown({
            cafeId: cafeCode,
            itemCount: currentItemCount,
            lineCount: currentItems.length,
            subtotal: currentSubtotal,
            grandTotal: currentSubtotal,
            hasTableNumber: Boolean(tableNumber),
            hasPhoneNumber: Boolean(phoneNumber),
          });
        }
        warningTimerRef.current = setTimeout(() => {
          clearCart({ clearSource: "cart_inactivity_timeout" });
          setCartExpiring(false);
        }, WARNING_MS);
      }
    }, 30000);
    return () => clearInterval(check);
  }, [items, cartExpiring, clearCart, cafeCode, tableNumber, phoneNumber, isCartTrackingEnabled]);

  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, [items.length]);

  const dismissExpiry = useCallback((meta = {}) => {
    setCartExpiring(false);
    lastActivityRef.current = Date.now();
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    const currentItems = items;
    const currentItemCount = currentItems.reduce((sum, it) => sum + it.quantity, 0);
    const currentSubtotal = currentItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0);
    if (isCartTrackingEnabled) {
      trackCartExpiryDismissed({
        cafeId: cafeCode,
        dismissSource: meta.dismissSource || "keep_cart",
        itemCount: currentItemCount,
        lineCount: currentItems.length,
        subtotal: currentSubtotal,
        grandTotal: currentSubtotal,
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, phoneNumber, tableNumber, isCartTrackingEnabled]);

  const getProductCartQty = useCallback((product) => {
    if (!product) return 0;
    const key = product?.id || product?.skuCode || product?.name;
    return items
      .filter((it) => it.productId === key)
      .reduce((sum, it) => sum + it.quantity, 0);
  }, [items]);

  const decreaseFromCart = useCallback((product) => {
    if (!product) return;
    const key = product?.id || product?.skuCode || product?.name;
    const candidate =
      items.find((it) => it.productId === key && !it.variantTitle) ||
      items.find((it) => it.productId === key);
    if (!candidate) return;
    updateQuantity(candidate.id, -1);
  }, [items, updateQuantity]);

  const replaceCartItem = useCallback((oldItemId, product, variant = null, selectedOptions = [], quantity = 1) => {
    if (!product) return;
    const addQty = Math.max(1, Math.floor(Number(quantity) || 1));
    const optionList = (Array.isArray(selectedOptions) ? selectedOptions : []).map(normalizeSelectedOption);
    const optionsExtra = optionList.reduce((sum, o) => sum + Number(o?.unitPrice ?? o?.price ?? 0), 0);
    const baseUnitPriceRaw = variant?.price ?? product?.price ?? 0;
    const baseUnitPrice = Number.isFinite(Number(baseUnitPriceRaw)) ? Number(baseUnitPriceRaw) : 0;
    const unitPrice = roundMoney(baseUnitPrice + optionsExtra);
    const id = createCartItemId(product, variant, optionList);
    const variantTitle = variant?.title || "";
    const name = variantTitle ? `${product.name} (${variantTitle})` : product.name;
    const skuCode = variant?.skuCode || product?.skuCode || "";
    const itemId = variant?.id || product?.id || product?.skuCode || product?.name;
    const imageUrl = product?.imageUrl || product?.productDefaultImage || "";
    const categoryName = product?.categoryName || product?.parentName || "";
    const subCategoryName = product?.subCategoryName || "";
    const taxTypeIds = Array.isArray(variant?.taxTypeIds)
      ? variant.taxTypeIds
      : Array.isArray(product?.taxTypeIds)
      ? product.taxTypeIds
      : [];
    const isPriceIncludesTax = Boolean(
      variant?.isPriceIncludesTax ?? product?.isPriceIncludesTax
    );

    const newItem = {
      id, itemId, productId: product?.id || product?.skuCode || product?.name,
      skuCode, name, shortName: product?.name || name, longName: name,
      productName: product?.name || "", categoryName, subCategoryName,
      variantTitle, selectedOptions: optionList, quantity: addQty,
      baseUnitPrice, optionTotal: roundMoney(optionsExtra), unitPrice,
      taxTypeIds, isPriceIncludesTax, imageUrl,
      tags: Array.isArray(product?.tags) ? product.tags : [],
    };

    const previousItem = items.find((it) => it.id === oldItemId) || null;
    const without = items.filter((it) => it.id !== oldItemId);
    const existing = without.findIndex((it) => it.id === id);
    const nextItems = existing !== -1
      ? without.map((it, i) => (i === existing ? { ...it, quantity: it.quantity + addQty } : it))
      : [...without, newItem];

    setItems(nextItems);

    const resultingItem = existing !== -1
      ? nextItems.find((it) => it.id === id)
      : newItem;

    if (isCartTrackingEnabled) {
      trackCartItemReplaced({
        cafeId: cafeCode,
        oldItem: previousItem,
        newItem: resultingItem,
        hasCustomization: optionList.length > 0 || Boolean(variantTitle),
        itemCount: nextItems.reduce((sum, it) => sum + it.quantity, 0),
        lineCount: nextItems.length,
        subtotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        grandTotal: nextItems.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
        hasTableNumber: Boolean(tableNumber),
        hasPhoneNumber: Boolean(phoneNumber),
      });
    }
  }, [cafeCode, items, tableNumber, phoneNumber, isCartTrackingEnabled]);

  const getProductCartLineCount = useCallback((product) => {
    if (!product) return 0;
    const key = product?.id || product?.skuCode || product?.name;
    return items.filter((it) => it.productId === key).length;
  }, [items]);

  const setTaxRates = useCallback((map) => setTaxRatesById(map || {}), []);

  const getItemTaxRatePercent = useCallback(
    (item) =>
      (item.taxTypeIds || [])
        .map((id) => {
          const taxType = taxRatesById[id];
          return Number(
            typeof taxType === "object" && taxType !== null
              ? taxType.percentage
              : taxType || 0
          );
        })
        .filter((v) => Number.isFinite(v))
        .reduce((sum, v) => sum + v, 0),
    [taxRatesById]
  );

  // GST % to charge on top for the server: 0 when the menu price already
  // includes tax, otherwise the item's menu tax rate.
  const getApiTaxRate = useCallback(
    (item) => (item.isPriceIncludesTax ? 0 : getItemTaxRatePercent(item)),
    [getItemTaxRatePercent]
  );

  // The moment the items differ from what the server holds, anything derived from the server copy
  // (its totals, an applied reward/offer) is out of date: drop it. The Review step saves again
  // and the Pay button waits for those fresh totals.
  useEffect(() => {
    const saved = savedCartRef.current;
    if (!saved.itemsKey) return;
    if (cartItemsKey(items, getApiTaxRate) !== saved.itemsKey) {
      setServerTotals(null);
      setAppliedReward(null);
      setAppliedDiscount(null);
    }
  }, [items, getApiTaxRate]);

  const repriceCartFromMenu = useCallback((priceIndex) => {
    if (!priceIndex || !priceIndex.skus) return;
    setItems((current) => {
      let changed = false;
      const next = current.map((item) => {
        const freshBase = priceIndex.skus[String(item.skuCode)];
        const options = (item.selectedOptions || []).map((option) => {
          const fresh = option?.id !== undefined ? priceIndex.options?.[String(option.id)] : undefined;
          if (fresh === undefined || roundMoney(fresh) === roundMoney(option.unitPrice ?? option.price ?? 0)) return option;
          changed = true;
          return { ...option, price: roundMoney(fresh), unitPrice: roundMoney(fresh) };
        });
        const base = freshBase === undefined ? item.baseUnitPrice : freshBase;
        if (freshBase !== undefined && roundMoney(freshBase) !== roundMoney(item.baseUnitPrice)) changed = true;
        const optionTotal = roundMoney(options.reduce((sum, o) => sum + Number(o.unitPrice ?? o.price ?? 0), 0));
        return { ...item, selectedOptions: options, baseUnitPrice: base, optionTotal, unitPrice: roundMoney(Number(base) + optionTotal) };
      });
      return changed ? next : current;
    });
  }, []);

  const getItemTaxAmount = useCallback(
    (item) => {
      const taxable = item.unitPrice * item.quantity;
      const rate = getItemTaxRatePercent(item);
      if (rate <= 0) return 0;
      if (item.isPriceIncludesTax) {
        return taxable - taxable / (1 + rate / 100);
      }
      return taxable * (rate / 100);
    },
    [getItemTaxRatePercent]
  );

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.unitPrice * it.quantity, 0),
    [items]
  );
  const taxTotal = useMemo(
    () => roundMoney(items.reduce((sum, it) => sum + getItemTaxAmount(it), 0)),
    [items, getItemTaxAmount]
  );
  const itemCount = items.reduce((sum, it) => sum + it.quantity, 0);
  const grandTotal = subtotal + taxTotal;

  const value = useMemo(
    () => ({
      items,
      hydrated,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      getProductCartQty,
      getProductCartLineCount,
      decreaseFromCart,
      replaceCartItem,
      createCartItemId,
      getSingleVariant,
      setTaxRates,
      serverTotals,
      getApiTaxRate,
      repriceCartFromMenu,
      getSavedCart,
      claimCartSave,
      finishCartSave,
      resetSavedCart,
      isCartTrackingEnabled,
      setCartTrackingEnabled,
      specialInstructions,
      setSpecialInstructions,
      phoneNumber,
      setPhoneNumber,
      tableNumber,
      setTableNumber,
      tableName,
      setTableName,
      orderType,
      setOrderType,
      appliedLoyaltyPoints,
      setAppliedLoyaltyPoints,
      appliedDiscount,
      setAppliedDiscount,
      appliedReward,
      setAppliedReward,
      cafeMenuCartId,
      serverTotals,
      setServerTotals,
      getApiTaxRate,
      repriceCartFromMenu,
      setCafeMenuCartId,
      itemCount,
      subtotal,
      taxTotal,
      grandTotal,
      cartExpiring,
      dismissExpiry,
      clearSignal,
    }),
    [
      items,
      hydrated,
      addToCart,
      updateQuantity,
      removeItem,
      clearCart,
      getProductCartQty,
      getProductCartLineCount,
      decreaseFromCart,
      replaceCartItem,
      setTaxRates,
      isCartTrackingEnabled,
      specialInstructions,
      phoneNumber,
      tableNumber,
      tableName,
      orderType,
      appliedLoyaltyPoints,
      appliedDiscount,
      appliedReward,
      cafeMenuCartId,
      serverTotals,
      getApiTaxRate,
      repriceCartFromMenu,
      getSavedCart,
      claimCartSave,
      finishCartSave,
      resetSavedCart,
      itemCount,
      subtotal,
      taxTotal,
      grandTotal,
      cartExpiring,
      dismissExpiry,
      clearSignal,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
